import { z } from "zod";

/**
 * Memberships (monthly) and packages (paid once). Both are catalog items with terms and
 * benefits: vouchers (prepaid visits) and discounts.
 */
export const PLAN_KINDS = ["membership", "package"] as const;
export type PlanKind = (typeof PLAN_KINDS)[number];

const uuids = (max: number) => z.array(z.string().uuid()).max(max).default([]);

export const voucherBenefitSchema = z.object({
  kind: z.literal("voucher"),
  /** Per month (memberships) or in all (packages). */
  quantity: z.number().int().min(1).max(100),
  /** Any one of these services or add-ons. */
  itemIds: z.array(z.string().uuid()).min(1, "Pick what the voucher covers").max(100),
  /** Months after issue; null = never expires. */
  expiresMonths: z.number().int().min(1).max(60).nullable().default(null),
  /** At most one used per this many days; null = no limit. */
  minDaysBetween: z.number().int().min(1).max(90).nullable().default(null),
});

export const discountBenefitSchema = z
  .object({
    kind: z.literal("discount"),
    percent: z.number().gt(0).max(100).nullable().default(null),
    /** Off each item. */
    amountCents: z.number().int().min(1).max(10_000_000).nullable().default(null),
    categoryIds: uuids(50),
    itemIds: uuids(300),
    /** Not discounted even when in a chosen category. */
    excludeItemIds: uuids(300),
  })
  .refine((d) => (d.percent === null) !== (d.amountCents === null), { message: "Enter a percent or an amount off", path: ["percent"] })
  .refine((d) => d.categoryIds.length + d.itemIds.length > 0, { message: "Pick what the discount applies to", path: ["categoryIds"] });

export const planBenefitSchema = z.union([voucherBenefitSchema, discountBenefitSchema]);
export type PlanBenefitInput = z.infer<typeof planBenefitSchema>;

export const planInputSchema = z
  .object({
    kind: z.enum(PLAN_KINDS),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(1000).default(""),
    /** Per month (memberships) or once (packages). */
    priceCents: z.number().int().min(0).max(10_000_000),
    /** Clients can buy it online. */
    soldOnline: z.boolean().default(true),
    minimumMonths: z.number().int().min(0).max(36).default(0),
    noticeDays: z.number().int().min(0).max(90).default(0),
    agreementText: z.string().trim().max(20_000).default(""),
    benefits: z.array(planBenefitSchema).min(1, "Add at least one benefit").max(10),
  })
  .superRefine((v, ctx) => {
    if (v.kind === "membership" && !v.agreementText) ctx.addIssue({ code: "custom", path: ["agreementText"], message: "Memberships need an agreement for clients to sign" });
  })
  .transform((v) => (v.kind === "package" ? { ...v, minimumMonths: 0, noticeDays: 0 } : v));
export type PlanInput = z.infer<typeof planInputSchema>;

export type PlanBenefit = PlanBenefitInput & { id: string };

export interface Plan {
  id: string;
  kind: PlanKind;
  name: string;
  description: string;
  priceCents: number;
  status: "active" | "archived";
  soldOnline: boolean;
  minimumMonths: number;
  noticeDays: number;
  agreementText: string;
  benefits: PlanBenefit[];
  updatedAt: string;
  /** The online sign-up page (when the clinic has a link name). */
  joinUrl?: string | null;
}

export const VOUCHER_EXPIRY_CHOICES: { months: number | null; label: string }[] = [
  { months: 1, label: "1 month after issue" },
  { months: 3, label: "3 months" },
  { months: 6, label: "6 months" },
  { months: 12, label: "1 year" },
  { months: null, label: "Never" },
];

/** "4 × The Base per month (max 1 per 7 days, expire after 1 month)". */
export function describeBenefit(b: PlanBenefitInput, kind: PlanKind, nameOf: (id: string) => string): string {
  if (b.kind === "voucher") {
    const what = b.itemIds.map(nameOf).join(" or ");
    const extras = [
      b.minDaysBetween ? `max 1 per ${b.minDaysBetween} day${b.minDaysBetween === 1 ? "" : "s"}` : null,
      b.expiresMonths ? `expire${b.quantity === 1 ? "s" : ""} after ${b.expiresMonths === 12 ? "1 year" : `${b.expiresMonths} month${b.expiresMonths === 1 ? "" : "s"}`}` : kind === "package" ? "never expire" : null,
    ].filter(Boolean);
    return `${b.quantity} × ${what}${kind === "membership" ? " per month" : ""}${extras.length ? ` (${extras.join(", ")})` : ""}`;
  }
  const off = b.percent !== null ? `${b.percent}% off` : `$${(b.amountCents! / 100).toFixed(2).replace(/\.00$/, "")} off`;
  const on = [...b.categoryIds, ...b.itemIds].map(nameOf).join(", ");
  const except = b.excludeItemIds.length ? `, except ${b.excludeItemIds.map(nameOf).join(", ")}` : "";
  return `${off} ${on}${except}`;
}

/** Whether a discount covers an item (by id or category, less exclusions). */
export function discountCovers(b: Extract<PlanBenefitInput, { kind: "discount" }>, item: { id: string; categoryId: string | null }): boolean {
  if (b.excludeItemIds.includes(item.id)) return false;
  return b.itemIds.includes(item.id) || (item.categoryId !== null && b.categoryIds.includes(item.categoryId));
}

/** Add months to a YYYY-MM-DD date; the 31st becomes the month's last day. */
export function addMonthsIso(date: string, months: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = total % 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return `${ny}-${String(nm + 1).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

// ── Clients' plans ──────────────────────────────────────────────────────

export type ClientPlanStatus = "pending" | "active" | "on_hold" | "ended";
export type VoucherStatus = "available" | "held" | "used" | "expired" | "forfeited";

export interface VoucherView {
  id: string;
  label: string;
  status: VoucherStatus;
  issuedAt: string;
  expiresAt: string | null;
  usedAt: string | null;
  /** The visit it's held for or was used on. */
  visit: { appointmentId: string; date: string; serviceName: string } | null;
  note: string | null;
}

export interface PlanChargeView {
  id: string;
  periodStart: string;
  periodEnd: string | null;
  amountCents: number;
  status: "pending" | "paid" | "failed" | "void";
  paidAt: string | null;
  failureMessage: string | null;
}

export interface ClientPlanView {
  id: string;
  planId: string;
  kind: PlanKind;
  name: string;
  priceCents: number;
  status: ClientPlanStatus;
  startedOn: string;
  nextBillingOn: string | null;
  commitmentEndsOn: string | null;
  cancelEffectiveOn: string | null;
  endedAt: string | null;
  endReason: string | null;
  card: { brand: string; last4: string } | null;
  agreementText: string;
  signedName: string | null;
  signedAt: string | null;
  signedVia: "desk" | "link" | "online" | null;
  soldBy: string | null;
  /** Vouchers that can still be used (available or held), and all of them. */
  vouchersLeft: number;
  vouchers: VoucherView[];
  charges: PlanChargeView[];
  /** A payment started at the desk that's still waiting (new card or reader). */
  pendingPaymentId: string | null;
}

export const SELL_METHODS = ["card_on_file", "card", "card_present", "cash"] as const;
export type SellMethod = (typeof SELL_METHODS)[number];

export const sellPlanSchema = z
  .object({
    planId: z.string().uuid(),
    method: z.enum(SELL_METHODS),
    savedCardId: z.string().uuid().nullable().default(null),
    readerId: z.string().uuid().nullable().default(null),
    cashReceivedCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
    /** The client typed their name to sign the agreement (when there is one). */
    signedName: z.string().trim().max(120).default(""),
    agreed: z.boolean().default(false),
  })
  .refine((v) => v.method !== "card_on_file" || v.savedCardId, { message: "Pick a card on file", path: ["savedCardId"] })
  .refine((v) => v.method !== "card_present" || v.readerId, { message: "Pick a card reader", path: ["readerId"] });
export type SellPlanInput = z.infer<typeof sellPlanSchema>;

/**
 * Bring over a membership already paid elsewhere (e.g. Boulevard): active now, nothing charged
 * until nextBillingOn, the old agreement counted as fulfilled (month to month, no minimum), no vouchers.
 */
export const transferPlanSchema = z.object({
  planId: z.string().uuid(),
  nextBillingOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick the next billing date"),
  savedCardId: z.string().uuid().nullable().default(null),
});
export type TransferPlanInput = z.infer<typeof transferPlanSchema>;

/** Staff send a client a link to add a card on file (for a membership: the card it's billed to). */
export const cardLinkSchema = z.object({
  channel: z.enum(["sms", "email"]).default("sms"),
  clientPlanId: z.string().uuid().nullable().default(null),
});
export type CardLinkInput = z.infer<typeof cardLinkSchema>;

export const PLAN_STATUS_LABELS: Record<ClientPlanStatus, string> = { pending: "Waiting for payment", active: "Active", on_hold: "On hold", ended: "Ended" };

/** Reports → Memberships: where memberships and packages stand today. */
export interface MembershipsReport {
  today: string;
  /** Active memberships per month (on hold not counted). */
  recurringCents: number;
  byPlan: { name: string; kind: PlanKind; active: number; onHold: number; cancelling: number; monthlyCents: number }[];
  onHold: { clientId: string; client: string; plan: string; since: string; amountCents: number; reason: string | null }[];
  cancelling: { clientId: string; client: string; plan: string; endsOn: string }[];
  /** Prepaid visits not used yet (available or held), valued at the covered item's clinic price. */
  vouchers: { plan: string; label: string; count: number; valueCents: number }[];
  /** Sold in the last 30 days. */
  recentSales: { date: string; clientId: string; client: string; plan: string; amountCents: number; via: "desk" | "link" | "online" | null }[];
}
